import { useEffect, useState } from 'react';
import { create } from 'zustand';
import {
  persist,
  createJSONStorage,
  subscribeWithSelector,
} from 'zustand/middleware';

const STORAGE_KEY = 'tenant.v1';
const STORE_VERSION = 1;
const HYDRATION_TIMEOUT_MS = 1000;

type TenantState = {
  version: number;
  tenantId: string | null;
  hasLoadedOnce: boolean;
  setTenantId: (tenantId: string) => void;
  reset: () => void;
};

export const useTenantStore = create<TenantState>()(
  persist(
    subscribeWithSelector((set) => ({
      version: STORE_VERSION,
      tenantId: null,
      hasLoadedOnce: false,

      setTenantId: (tenantId) =>
        set({
          tenantId,
          hasLoadedOnce: true,
        }),

      reset: () =>
        set({
          tenantId: null,
          hasLoadedOnce: false,
        }),
    })),
    {
      name: STORAGE_KEY,
      version: STORE_VERSION,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        version: state.version,
        tenantId: state.tenantId,
        hasLoadedOnce: state.hasLoadedOnce,
      }),
    },
  ),
);

export const useTenantId = (): string | null =>
  useTenantStore((state) => state.tenantId);

export const useHasLoadedTenantOnce = (): boolean =>
  useTenantStore((state) => state.hasLoadedOnce);

export type TenantHydrationStatus =
  | { state: 'pending' }
  | { state: 'ready' }
  | { state: 'error'; reason: 'timeout' };

export const useTenantHydrationStatus = (): TenantHydrationStatus => {
  const [status, setStatus] = useState<TenantHydrationStatus>(
    useTenantStore.persist.hasHydrated()
      ? { state: 'ready' }
      : { state: 'pending' },
  );
  useEffect(() => {
    const unsub = useTenantStore.persist.onFinishHydration(() =>
      setStatus({ state: 'ready' }),
    );
    if (useTenantStore.persist.hasHydrated()) setStatus({ state: 'ready' });
    const fallback = setTimeout(() => {
      if (!useTenantStore.persist.hasHydrated()) {
        console.warn(
          '[tenantStore] persist hydration timed out — localStorage may be unavailable',
        );
        setStatus({ state: 'error', reason: 'timeout' });
      }
    }, HYDRATION_TIMEOUT_MS);
    return () => {
      unsub();
      clearTimeout(fallback);
    };
  }, []);
  return status;
};

export const getTenantId = (): string | null =>
  useTenantStore.getState().tenantId;

export const requireTenantId = (): string => {
  const tenantId = useTenantStore.getState().tenantId;
  if (!tenantId) {
    throw new Error(
      'Tenant id has not been resolved yet — make sure TenantGate has loaded before this call.',
    );
  }
  return tenantId;
};
